/* ─────────────────────────────────────────────────────────
 * ANIMATION STORYBOARD
 *
 *    0ms   touchdowns bar fills toward the leading team
 *  100ms   casualties bar fills
 *  200ms   completions bar fills
 *  300ms   possessions bar fills
 *          (each fill runs 0.8s, decelerating)
 * ───────────────────────────────────────────────────────── */

import { Skull, Swords, Target, Trophy } from "lucide-react"

import type { MatchPageProps, MatchStats } from "@/types"

const TIMING = {
  barStagger: 100, // ms between successive stat bars
  barFill: 0.8, // s — bar width fill duration
  barEase: "cubic-bezier(0.25, 1, 0.5, 1)", // decelerating fill
}

interface StatBarProps {
  label: string
  icon: React.ComponentType<{ className?: string }>
  homeVal: number
  awayVal: number
  homeHue: number
  awayHue: number
  delay: number
}

function StatBar({
  label,
  icon: Icon,
  homeVal,
  awayVal,
  homeHue,
  awayHue,
  delay,
}: StatBarProps) {
  const total = homeVal + awayVal
  const homePercent = total > 0 ? (homeVal / total) * 100 : 50
  const awayPercent = total > 0 ? (awayVal / total) * 100 : 50
  const homeWins = homeVal > awayVal
  const awayWins = awayVal > homeVal

  const homeColor = `oklch(0.55 0.18 ${homeHue})`
  const awayColor = `oklch(0.55 0.18 ${awayHue})`
  const homeGlow = `oklch(0.60 0.20 ${homeHue} / 0.5)`
  const awayGlow = `oklch(0.60 0.20 ${awayHue} / 0.5)`

  return (
    <div className="mb-4">
      {/* Label row */}
      <div className="mb-1.5 flex items-center justify-between">
        <span
          className="font-display text-2xl font-bold tabular-nums"
          style={{
            color: homeWins ? homeColor : "var(--muted-foreground)",
            textShadow: homeWins ? `0 0 12px ${homeGlow}` : "none",
          }}
        >
          {homeVal}
        </span>
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
          <span className="text-[10px] font-bold tracking-widest text-[var(--muted-foreground)] uppercase">
            {label}
          </span>
        </div>
        <span
          className="font-display text-2xl font-bold tabular-nums"
          style={{
            color: awayWins ? awayColor : "var(--muted-foreground)",
            textShadow: awayWins ? `0 0 12px ${awayGlow}` : "none",
          }}
        >
          {awayVal}
        </span>
      </div>

      {/* Bar */}
      <div
        className="flex h-2.5 overflow-hidden"
        style={{ background: "oklch(0.15 0.01 55)" }}
      >
        <div
          className="stat-bar-fill h-full"
          style={{
            width: `${homePercent}%`,
            background: homeColor,
            boxShadow: homeWins ? `0 0 12px ${homeGlow}` : "none",
            transition: `width ${TIMING.barFill}s ${TIMING.barEase} ${delay}ms`,
          }}
        />
        <div
          className="h-full w-px flex-shrink-0"
          style={{ background: "oklch(0.25 0.02 55)" }}
        />
        <div
          className="stat-bar-fill h-full"
          style={{
            width: `${awayPercent}%`,
            background: awayColor,
            boxShadow: awayWins ? `0 0 12px ${awayGlow}` : "none",
            transition: `width ${TIMING.barFill}s ${TIMING.barEase} ${delay}ms`,
          }}
        />
      </div>
    </div>
  )
}

interface StatComparisonBarsProps {
  stats: MatchStats
  match: MatchPageProps["match"]
}

export function StatComparisonBars({ stats, match }: StatComparisonBarsProps) {
  const homeHue = match.home_team.color_hue ?? 55
  const awayHue = match.away_team.color_hue ?? 280

  const statDefs = [
    { key: "touchdowns" as const, label: "Touchdowns", icon: Trophy },
    { key: "casualties" as const, label: "Casualties", icon: Skull },
    { key: "completions" as const, label: "Completions", icon: Target },
    { key: "possessions" as const, label: "Possessions", icon: Swords },
  ]

  return (
    <div>
      {/* Team name anchors */}
      <div className="mb-4 flex items-center justify-between">
        <span
          className="text-xs font-bold tracking-widest uppercase"
          style={{ color: `oklch(0.70 0.16 ${homeHue})` }}
        >
          {match.home_team.name}
        </span>
        <span
          className="text-xs font-bold tracking-widest uppercase"
          style={{ color: `oklch(0.70 0.16 ${awayHue})` }}
        >
          {match.away_team.name}
        </span>
      </div>

      {statDefs.map(({ key, label, icon }, idx) => (
        <StatBar
          key={key}
          label={label}
          icon={icon}
          homeVal={stats.home[key]}
          awayVal={stats.away[key]}
          homeHue={homeHue}
          awayHue={awayHue}
          delay={idx * TIMING.barStagger}
        />
      ))}
    </div>
  )
}
